import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Progress,
} from "reactstrap";
import {
  getPasswordStrength,
  getPasswordStrengthColor,
} from "./CommonFunctions";

const initialState = {
  currentPassword: {
    value: "",
    error: "",
  },
  newPassword: {
    value: "",
    error: "",
  },
  confirmPassword: {
    value: "",
    error: "",
  },
};

const ChangePasswordModal = ({ isOpen, toggle }) => {
  const [passwordForm, setPasswordForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  useEffect(() => {
    if (!isOpen) {
      setPasswordForm(initialState);
      setLoading(false);
      setShow({
        current: false,
        new: false,
        confirm: false,
      });
    }
  }, [isOpen]);

  const toggleVisibility = (key) => {
    setShow((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleChange = (fieldName, newValue) => {
    setPasswordForm((prev) => ({
      ...prev,
      [fieldName]: {
        value: newValue,
        error: "",
      },
    }));
  };

  const validatePasswordForm = () => {
    let newPassword = { ...passwordForm };

    Object.keys(newPassword).forEach((key) => {
      newPassword[key] = {
        ...newPassword[key],
        error: getValidationPasswordError(key, newPassword[key].value),
      };
    });

    setPasswordForm(newPassword); // set the error state
    return Object.values(newPassword).every((item) => item.error === "");
  };

  const getValidationPasswordError = (name, value) => {
    switch (name) {
      case "currentPassword":
        return !value ? "Please enter current password" : "";

      case "newPassword":
        return !value
          ? "Please enter a new password"
          : value.length < 8
          ? "The password must contain 8 characters"
          : passwordForm.confirmPassword.value &&
            value !== passwordForm.confirmPassword.value
          ? "The new password and confirm password must match"
          : "";
      case "confirmPassword":
        return !value
          ? "Please enter a confirm password"
          : passwordForm.newPassword.value &&
            value !== passwordForm.newPassword.value
          ? "The new password and confirm password must match"
          : "";
      default:
        return "";
    }
  };
  const handleOnBlur = (name, value) => {
    const error = getValidationPasswordError(name, value);
    // console.log("handle blur ", error, value);
    setPasswordForm((prev) => ({
      ...prev,
      [name]: { ...prev[name], error },
    }));
  };

  const handleSubmit = () => {
    if (!validatePasswordForm()) return;
    setLoading(true);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle} className="border-0">
        <div className="w-100">
          <div className="change-password-header">
            <i className="fa-solid fa-lock change-password-icon mb-3" />
            <h2 className="mt-2">Change Password</h2>
            <span className="text-muted">
              Choose a strong password to keep your account secure
            </span>
          </div>
        </div>
      </ModalHeader>
      <ModalBody>
        <div className="change-password-body">
          {/* Current Password */}
          <FormGroup>
            <Label>Current Password</Label>
            <InputGroup className="input-group-merge">
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <i className="fa-solid fa-lock" />
                </InputGroupText>
              </InputGroupAddon>

              <Input
                name="currentPassword"
                type={show.current ? "text" : "password"}
                value={passwordForm.currentPassword.value}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                className={
                  passwordForm.currentPassword.error ? "is-invalid" : ""
                }
              />

              <InputGroupAddon addonType="append">
                <InputGroupText
                  className="cursor-pointer"
                  onClick={() => toggleVisibility("current")}
                >
                  <i
                    className={`fa ${show.current ? "fa-eye-slash" : "fa-eye"}`}
                  />
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>

            {passwordForm.currentPassword.error && (
              <div className="invalid-feedback d-block">
                {passwordForm.currentPassword.error}
              </div>
            )}
          </FormGroup>

          {/* New Password */}
          <FormGroup>
            <Label>New Password</Label>
            <InputGroup className="input-group-merge">
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <i className="fa-solid fa-lock" />
                </InputGroupText>
              </InputGroupAddon>

              <Input
                name="newPassword"
                type={show.new ? "text" : "password"}
                value={passwordForm.newPassword.value}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                maxLength={16}
                required
                className={passwordForm.newPassword.error ? "is-invalid" : ""}
              />

              <InputGroupAddon addonType="append">
                <InputGroupText
                  className="cursor-pointer"
                  onClick={() => toggleVisibility("new")}
                >
                  <i className={`fa ${show.new ? "fa-eye-slash" : "fa-eye"}`} />
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>
            {passwordForm.newPassword.value && (
              <div className="mt-2">
                <Progress
                  value={
                    getPasswordStrength(passwordForm.newPassword.value) ===
                    "Strong"
                      ? 100   //for progress bar we are taking 100 - 60 - 30 
                      : getPasswordStrength(passwordForm.newPassword.value) ===
                        "Medium"
                      ? 60
                      : 30
                  }
                  color={
                    getPasswordStrength(passwordForm.newPassword.value) ===
                    "Strong"
                      ? "success"
                      : getPasswordStrength(passwordForm.newPassword.value) ===
                        "Medium"
                      ? "warning"
                      : "danger"
                  }
                  className="mb-1"
                />

                <small
                  className={`pl-2 ${getPasswordStrengthColor(
                    getPasswordStrength(passwordForm.newPassword.value)
                  )}`}
                >
                  {getPasswordStrength(passwordForm.newPassword.value)} password
                </small>
              </div>
            )}

            {passwordForm.newPassword.error && (
              <div className="invalid-feedback d-block">
                {passwordForm.newPassword.error}
              </div>
            )}
          </FormGroup>

          {/* Confirm Password */}
          <FormGroup>
            <Label>Confirm New Password</Label>
            <InputGroup className="input-group-merge">
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <i className="fa-solid fa-lock" />
                </InputGroupText>
              </InputGroupAddon>

              <Input
                name="confirmPassword"
                type={show.confirm ? "text" : "password"}
                value={passwordForm.confirmPassword.value}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                maxLength={16}
                required
                className={
                  passwordForm.confirmPassword.error ? "is-invalid" : ""
                }
              />

              <InputGroupAddon addonType="append">
                <InputGroupText
                  className="cursor-pointer"
                  onClick={() => toggleVisibility("confirm")}
                >
                  <i
                    className={`fa ${show.confirm ? "fa-eye-slash" : "fa-eye"}`}
                  />
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>

            {passwordForm.confirmPassword.error && (
              <div className="invalid-feedback d-block">
                {passwordForm.confirmPassword.error}
              </div>
            )}
          </FormGroup>

          <div className="security-note text-muted mt-3">
            <i className="fa fa-shield-alt mr-1" />
            <span>Your password is encrypted and securely stored</span>
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          color="primary"
          className="btn-vivid-red w-100 mt-n3 p-3"
          onClick={handleSubmit}
          disabled={loading}
        >
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ChangePasswordModal;
